/* /new — start a review.
 *
 * Three things are visible: the dropzone, the DOE example, and any review this
 * browser can still open. Nothing else. BRIEF.md defect 6 was that this page
 * put a 39-section parse listing, a "confirm the parse" step and a "Run checks"
 * button between a reviewer and the first thing worth reading. All three are
 * gone. The listing moved to the workspace's right rail in step 9; the
 * confirmation was work that served the engine; and there is nothing to press
 * "Run checks" for, because reading a document and checking it are one
 * movement now.
 *
 * The run itself lives in src/data/analysisRun.ts, outside React, because it
 * has to survive this page: the reviewer is taken to the workspace as soon as
 * there is a finding in it, and the remaining checks keep streaming in behind
 * them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Label, Spinner } from '../components/primitives';
import { DeleteReview } from '../components/workspace';
import {
  BUNDLED_EXAMPLE,
  checkFile,
  ingestBundledExample,
  ingestPdf,
} from '../engine/ingest';
import {
  activeRun,
  readyToOpen,
  startAnalysis,
  type AnalysisRunState,
  type RunStep,
} from '../data/analysisRun';
import { deleteReview } from '../data/store';
import {
  offersExample,
  readRestorable,
  restoredLine,
  restoredMeta,
  stateOf,
  type RestorableReview,
} from './newReviewModel';
import type { IngestRejection } from '../types';
import './NewReview.css';

export default function NewReview() {
  const navigate = useNavigate();
  const [run, setRun] = useState<AnalysisRunState | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [restorable, setRestorable] = useState<RestorableReview[] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The navigation happens once. Findings keep arriving after it, and each
  // arrival publishes; without this the workspace would be pushed again on
  // every one of them.
  const navigated = useRef(false);
  // The run outlives this page on purpose, so the subscription has to be let go
  // of when the page goes: the run keeps going, this component stops listening.
  const watching = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      watching.current?.();
      watching.current = null;
    },
    [],
  );

  /* --- reviews this browser can still open ------------------------------ */

  const loadRestorable = useCallback(async () => {
    try {
      setRestorable(await readRestorable());
    } catch {
      // A browser with storage blocked has nothing to restore, which is the
      // truth and not an error worth a panel of its own.
      setRestorable([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // A run already in flight — the reviewer pressed Back out of a workspace
    // that was still filling — is picked up rather than restarted.
    const existing = activeRun();
    if (existing && existing.state.status === 'running') {
      setRun(existing.state);
      const unsubscribe = existing.subscribe((next) => {
        if (!cancelled) setRun(next);
      });
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    void loadRestorable();
    return () => {
      cancelled = true;
    };
  }, [loadRestorable]);

  /* --- starting one ------------------------------------------------------ */

  const start = useCallback(
    (fileName: string, ingest: Parameters<typeof startAnalysis>[0]['ingest']) => {
      navigated.current = false;
      watching.current?.();
      const started = startAnalysis({ fileName, ingest });
      setRun(started.state);
      watching.current = started.subscribe((next) => {
        setRun(next);
        // As soon as there is something in the workspace worth reading, the
        // reviewer goes to it. The rest of the checks arrive there.
        if (!navigated.current && readyToOpen(next)) {
          navigated.current = true;
          navigate(`/review/${next.docId}`);
        }
      });
    },
    [navigate],
  );

  const handleFile = useCallback(
    (file: File) => {
      // Type and size are known before a byte is read, so they are answered
      // before the page changes shape.
      const rejected = checkFile(file);
      if (rejected && !rejected.ok) {
        setRun({
          docId: null,
          fileName: file.name,
          status: 'rejected',
          steps: [],
          counts: {},
          document: null,
          flags: [],
          ranChecks: [],
          skipped: [],
          failures: [],
          rejection: rejected.rejection,
        });
        return;
      }

      start(file.name, async (onProgress) => {
        let data: ArrayBuffer;
        try {
          data = await file.arrayBuffer();
        } catch {
          return {
            ok: false,
            rejection: {
              reason: 'unreadable',
              message:
                'This file couldn’t be opened (the browser could not read its bytes). Nothing left your browser.',
              detail:
                'This usually means the file moved, or the drive it is on went away, between choosing it and reading it. Selecting it again normally works.',
            },
          };
        }
        return ingestPdf(file, data, onProgress);
      });
    },
    [start],
  );

  const handleExample = useCallback(() => {
    start(BUNDLED_EXAMPLE.fileName, (onProgress) => ingestBundledExample(onProgress));
  }, [start]);

  /**
   * Deleting a review from the list. The confirmation is inside DeleteReview
   * and names what goes; this is only what happens once it has been given.
   */
  const removeReview = useCallback(
    async (docId: string) => {
      setDeletingId(docId);
      try {
        await deleteReview(docId);
      } catch {
        // Nothing to delete, or nowhere to delete it from.
      }
      await loadRestorable();
      setDeletingId(null);
    },
    [loadRestorable],
  );

  const state = stateOf(run);
  const busy = state === 'working';
  const rejection = run?.rejection ?? null;

  return (
    <div className="nr">
      <section className="nr__head">
        <h1 className="t-display-2">Start a review</h1>
      </section>

      <section className="nr__intake">
        <div className="nr__intake-main">
          {busy && run ? (
            <Progress run={run} />
          ) : (
            <>
              {rejection ? (
                <Refusal
                  rejection={rejection}
                  onExample={offersExample(rejection) ? handleExample : undefined}
                />
              ) : null}

              <div
                className={`nr__drop${dragOver ? ' nr__drop--over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFile(file);
                }}
              >
                <span className="nr__drop-head">Drop a PDF here</span>
                <Button
                  variant="secondary"
                  marker
                  onClick={() => inputRef.current?.click()}
                >
                  Choose a file
                </Button>
                {/* The real control, kept out of the layout and out of the
                    tab order: "Choose a file" above opens the same picker, and
                    two stops for one action is one too many. It still carries
                    a name, because a reader browsing the page will find it. */}
                <input
                  ref={inputRef}
                  className="nr__file-input"
                  type="file"
                  tabIndex={-1}
                  aria-label="Choose a PDF to review"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Reset so selecting the same file twice still fires.
                    e.target.value = '';
                    if (file) handleFile(file);
                  }}
                />
              </div>

              <p className="nr__hint">
                One M&amp;V plan or investment grade audit, up to 50 MB. It needs a
                real text layer — a file you can select text in, not a scan.
              </p>
              <p className="nr__privacy">
                Your document is read in this browser. It is never uploaded.
              </p>
            </>
          )}
        </div>

        <div className="nr__intake-alt">
          <Label mono>No plan to hand?</Label>
          <p className="nr__alt-body">
            Run the DOE&rsquo;s own example M&amp;V plan — the document the
            government publishes as the model of doing this correctly.
          </p>
          <Button variant="primary" marker disabled={busy} onClick={handleExample}>
            Run the example plan
          </Button>
        </div>
      </section>

      {!busy && restorable && restorable.length > 0 ? (
        <Restorable
          rows={restorable}
          onDelete={removeReview}
          busyId={deletingId}
        />
      ) : null}
    </div>
  );
}

/* ==========================================================================
   Progress
   --------------------------------------------------------------------------
   In the dropzone's place, not beneath it: what the page is doing is the whole
   of what the page is for while it is doing it.
   ========================================================================== */

function Progress({ run }: { run: AnalysisRunState }) {
  const current = run.steps.find((s) => s.state === 'running') ?? null;
  const fraction = useMemo(() => {
    const settled = run.steps.filter(
      (s) => s.state === 'done' || s.state === 'skipped' || s.state === 'failed',
    ).length;
    return run.steps.length === 0 ? 0 : settled / run.steps.length;
  }, [run.steps]);

  return (
    <div className="nr__progress">
      <div className="nr__progress-head">
        <Spinner label={current ? current.label : 'Finishing'} />
        <span className="nr__progress-name">{run.fileName}</span>
      </div>

      <ol className="nr__steps" aria-live="polite">
        {run.steps.map((step) => (
          <Step key={step.id} step={step} />
        ))}
      </ol>

      <div className="nr__meter" aria-hidden="true">
        <div
          className="nr__meter-fill"
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </div>

      <p className="nr__progress-note">
        {run.flags.length > 0
          ? `${run.flags.length} finding${run.flags.length === 1 ? '' : 's'} so far. Opening the review.`
          : current
            ? 'The review opens as soon as there is something in it to read.'
            : 'Finishing.'}
      </p>
    </div>
  );
}

const STEP_MARKS: Record<RunStep['state'], string> = {
  waiting: '·',
  running: '▸',
  done: '✓',
  skipped: '—',
  failed: '×',
};

function Step({ step }: { step: RunStep }) {
  return (
    <li className={`nr__step nr__step--${step.state}`}>
      <span className="nr__step-mark" aria-hidden="true">
        {STEP_MARKS[step.state]}
      </span>
      <span className="nr__step-label">{step.label}</span>
      {step.note ? <span className="nr__step-note">{step.note}</span> : null}
    </li>
  );
}

/* ==========================================================================
   A file that was refused
   --------------------------------------------------------------------------
   What happened, what it means, what to do next. The dropzone stays live
   underneath: the answer to nearly all of these is another file.
   ========================================================================== */

function Refusal({
  rejection,
  onExample,
}: {
  rejection: IngestRejection;
  onExample?: () => void;
}) {
  return (
    <div className="nr__refusal" role="alert">
      <Label tone="amber">Not read</Label>
      <h2 className="nr__refusal-head">{rejection.message}</h2>
      {rejection.detail ? (
        <p className="nr__refusal-body">{rejection.detail}</p>
      ) : null}
      {onExample ? (
        <Button variant="secondary" marker onClick={onExample}>
          Run the DOE example plan instead
        </Button>
      ) : null}
    </div>
  );
}

/* ==========================================================================
   Reviews already in this browser
   ========================================================================== */

function Restorable({
  rows,
  onDelete,
  busyId,
}: {
  rows: RestorableReview[];
  onDelete(docId: string): void;
  /** The review currently being deleted, so its control cannot be pressed twice. */
  busyId: string | null;
}) {
  return (
    <section className="nr__restorable" aria-label="Reviews in this browser">
      <Label mono>In progress</Label>
      <ul className="nr__reviews">
        {rows.map((row) => (
          <li key={row.docId} className="nr__review">
            <div className="nr__review-text">
              <p className="nr__review-line">{restoredLine(row)}</p>
              <p className="nr__review-meta">{restoredMeta(row)}</p>
            </div>
            <div className="nr__review-actions">
              <Button variant="primary" marker to={`/review/${row.docId}`}>
                Resume
              </Button>
              <DeleteReview
                variant="row"
                busy={busyId === row.docId}
                onDelete={() => onDelete(row.docId)}
              />
            </div>
          </li>
        ))}
      </ul>
      <p className="nr__reviews-note">
        Reviews are held in this browser and nowhere else. Deleting one removes
        the document, the page text Ceiling extracted from it, the findings and
        every decision on them, and cannot be undone.
      </p>
    </section>
  );
}
